import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { WorkflowInputLayout } from './WorkflowInputLayout';
import { getMdhdWorkflowConfig, updateMdhdWorkflowConfig } from '../../../utils/mdhdWorkflowConfig';
import { API_ENDPOINTS } from '../../../config/apiConfig';

const MAX_SELECTED_VEHICLES = 5;

function normalizeVehicleId(vehicle) {
  return vehicle?.vehicle_id ?? vehicle?.id ?? null;
}

function formatPrice(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 'N/A';
  return `$${n.toLocaleString()}`;
}

export default function VehicleConfigurationInputPage() {
  const workflowConfig = getMdhdWorkflowConfig().vehicleConfiguration;
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVehicleIds, setSelectedVehicleIds] = useState(
    Array.isArray(workflowConfig.selectedVehicleIds) ? workflowConfig.selectedVehicleIds : []
  );

  useEffect(() => {
    let cancelled = false;
    const fetchVehicles = async () => {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setError('Missing auth token. Please log in again.');
        setLoading(false);
        return;
      }
      setLoading(true);
      setError('');
      try {
        const res = await axios.get(API_ENDPOINTS.getVehicleData, {
          headers: { Authorization: `Token ${token}` },
        });
        if (cancelled) return;
        const rows = Array.isArray(res.data?.vehicles) ? res.data.vehicles : (Array.isArray(res.data) ? res.data : []);
        const mdhdRows = rows.filter((vehicle) => {
          const vehicleClass = String(vehicle?.vehicle_class || '').toLowerCase();
          const category = String(vehicle?.category || '').toLowerCase();
          return (
            vehicleClass.includes('medium') ||
            vehicleClass.includes('heavy') ||
            category.includes('md') ||
            category.includes('hd') ||
            category.includes('truck')
          );
        });
        setVehicles(mdhdRows);
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.error || err.message || 'Failed to load vehicles.');
          setVehicles([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchVehicles();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    updateMdhdWorkflowConfig('vehicleConfiguration', { selectedVehicleIds });
  }, [selectedVehicleIds]);

  const filteredVehicles = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return vehicles;
    return vehicles.filter((vehicle) =>
      [vehicle?.make, vehicle?.model, vehicle?.vehicle_class, vehicle?.category]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q))
    );
  }, [vehicles, searchTerm]);

  const selectedCount = selectedVehicleIds.length;

  const handleToggleVehicle = (vehicleId) => {
    if (vehicleId == null) return;
    const normalizedId = String(vehicleId);
    setSelectedVehicleIds((prev) => {
      const exists = prev.some((id) => String(id) === normalizedId);
      if (exists) return prev.filter((id) => String(id) !== normalizedId);
      if (prev.length >= MAX_SELECTED_VEHICLES) return prev;
      return [...prev, vehicleId];
    });
  };

  return (
    <WorkflowInputLayout title="Vehicle configuration">
      <div className="bg-white rounded-xl shadow-lg font-nunito p-6">
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-gray-800">Simulation assumptions</h2>
          <p className="text-sm text-gray-600 mt-1">
            Select up to 5 MDHD vehicle models from the DataHub vehicle database for this workflow scenario.
          </p>
        </div>

        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <label className="text-sm font-semibold text-gray-800">
              MDHD vehicles ({selectedCount}/{MAX_SELECTED_VEHICLES} selected)
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search make, model, class..."
              className="w-full sm:w-72 rounded border border-gray-300 px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {error && (
            <div className="mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          {selectedCount >= MAX_SELECTED_VEHICLES && (
            <div className="mb-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              Maximum of {MAX_SELECTED_VEHICLES} vehicles selected. Deselect one to choose another model.
            </div>
          )}

          <div className="max-h-[480px] overflow-auto rounded border border-gray-200 bg-white">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-600 uppercase">Select</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600 uppercase">Make & Model</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600 uppercase">Year</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600 uppercase">Class</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-600 uppercase">Battery (kWh)</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-600 uppercase">Range (mi)</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-600 uppercase">Max Charge (kW)</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-600 uppercase">Price</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {loading ? (
                  <tr>
                    <td colSpan={8} className="px-3 py-6 text-center text-gray-500">
                      Loading vehicle models...
                    </td>
                  </tr>
                ) : filteredVehicles.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-3 py-6 text-center text-gray-500">
                      {vehicles.length === 0 ? (
                        <span>
                          No MDHD vehicle data found in the database.
                          <br />
                          <span className="text-xs text-gray-400 mt-1 block">
                            {/* Run <code className="bg-gray-100 px-1 rounded">python manage.py import_vehicle_data --skip-hvip</code> inside the backend container to populate the vehicle catalogue. */}
                          </span>
                        </span>
                      ) : (
                        'No vehicles match your search.'
                      )}
                    </td>
                  </tr>
                ) : (
                  filteredVehicles.map((vehicle) => {
                    const vehicleId = normalizeVehicleId(vehicle);
                    if (vehicleId == null) return null;
                    const isSelected = selectedVehicleIds.some((id) => String(id) === String(vehicleId));
                    const disableUnchecked = !isSelected && selectedCount >= MAX_SELECTED_VEHICLES;
                    return (
                      <tr key={String(vehicleId)} className={isSelected ? 'bg-blue-50/40' : ''}>
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={disableUnchecked}
                            onChange={() => handleToggleVehicle(vehicleId)}
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-3 py-2 text-gray-800 font-medium">
                          {`${vehicle.make || ''} ${vehicle.model || ''}`.trim() || 'Unknown model'}
                        </td>
                        <td className="px-3 py-2 text-gray-700">{vehicle.model_year || 'N/A'}</td>
                        <td className="px-3 py-2 text-gray-700">{vehicle.vehicle_class || vehicle.category || 'N/A'}</td>
                        <td className="px-3 py-2 text-right text-gray-700">
                          {Number.isFinite(Number(vehicle.batt_cap)) ? Number(vehicle.batt_cap).toFixed(1) : 'N/A'}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-700">
                          {Number.isFinite(Number(vehicle.operation_range)) ? Number(vehicle.operation_range).toFixed(0) : 'N/A'}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-700">
                          {Number.isFinite(Number(vehicle.max_charging_power)) ? Number(vehicle.max_charging_power).toFixed(0) : 'N/A'}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-700">{formatPrice(vehicle.price)}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </WorkflowInputLayout>
  );
}
